<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { Head, Link } from '@inertiajs/inertia-vue3';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import { Icon } from '@iconify/vue';
import axios from 'axios';
import * as XLSX from 'xlsx';

import DoughnutChart from '@/Components/Charts/DoughnutChart.vue';
import BarChart from '@/Components/Charts/BarChart.vue';
import LineChart from '@/Components/Charts/LineChart.vue';
import MixedBarLineChart from '@/Components/Charts/MixedBarLineChart.vue';

const props = defineProps({
    collaborators: {
        type: Array,
        default: () => []
    },
    initialSelectedId: {
        type: Number,
        default: 0
    },
    initialMes: {
        type: Number,
        default: () => new Date().getMonth() + 1
    },
    initialAnio: {
        type: Number,
        default: () => new Date().getFullYear()
    },
    initialData: {
        type: Object,
        default: null
    }
});

// Estado de filtros
const selectedCollaboratorId = ref(props.initialSelectedId);
const selectedMes = ref(props.initialMes);
const selectedAnio = ref(props.initialAnio);
const loading = ref(false);
const balanceData = ref(props.initialData);
const activeTab = ref('detalle'); // 'detalle' | 'graficos'

// Opciones de meses
const meses = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' }
];

// Opciones de años (actual - 4 hasta actual + 1)
const anios = computed(() => {
    const curr = new Date().getFullYear();
    const list = [];
    for (let y = curr - 4; y <= curr + 1; y++) {
        list.push(y);
    }
    return list;
});

// Consulta asíncrona de datos
const fetchBalanceData = async () => {
    if (!selectedCollaboratorId.value) return;
    loading.value = true;
    try {
        const response = await axios.post(route('rrhh.asistencia-balance-empleado.data'), {
            id_collaborator: selectedCollaboratorId.value,
            mes: selectedMes.value,
            anio: selectedAnio.value
        });
        if (response.data.success) {
            balanceData.value = response.data.data;
        }
    } catch (error) {
        console.error('Error al cargar balance de asistencia:', error);
    } finally {
        loading.value = false;
    }
};

// Cargar al montar si no vino en props iniciales
onMounted(() => {
    if (!balanceData.value && selectedCollaboratorId.value) {
        fetchBalanceData();
    }
});

// Helpers de formateo
const getMesNombre = (m) => {
    const item = meses.find(x => x.value === m);
    return item ? item.label : m;
};

const getStatusBadge = (estado) => {
    switch (estado) {
        case 'puntual':
            return { label: 'Puntual', class: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' };
        case 'tardanza':
            return { label: 'Tardanza', class: 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300 border-amber-200 dark:border-amber-800' };
        case 'falta':
            return { label: 'Inasistencia', class: 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300 border-rose-200 dark:border-rose-800' };
        case 'extra':
            return { label: 'Día Extra', class: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800' };
        case 'feriado':
            return { label: 'Feriado', class: 'bg-purple-50 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300 border-purple-200 dark:border-purple-800' };
        case 'permiso':
            return { label: 'Permiso / Libre', class: 'bg-sky-50 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300 border-sky-200 dark:border-sky-800' };
        default:
            return { label: 'Descanso', class: 'bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-400 border-slate-200 dark:border-slate-700' };
    }
};

// Exportar a Excel
const exportToExcel = () => {
    if (!balanceData.value) return;

    const data = balanceData.value;
    const rows = [
        ['INFORME INDIVIDUAL DE CONTROL Y BALANCE DE ASISTENCIA'],
        ['Colaborador:', data.colaborador.nombre_completo],
        ['Documento:', data.colaborador.num_doc],
        ['Puesto / Cargo:', data.colaborador.puesto],
        ['Turno Programado:', data.colaborador.turno_nombre],
        ['Periodo Consultado:', `${getMesNombre(data.mes)} ${data.anio}`],
        [],
        ['RESUMEN DE HORAS Y COMPENSACIÓN'],
        ['Horas Programadas:', data.periodo_actual.total_horas_programadas],
        ['Horas Laboradas Reales:', data.periodo_actual.total_horas_laboradas],
        ['Horas Extras (+):', data.periodo_actual.total_horas_extra],
        ['Horas Debe (-):', data.periodo_actual.total_horas_debe],
        ['Saldo Neto del Mes:', data.periodo_actual.saldo_neto],
        ['Saldo Acumulado Anterior:', data.balance_acumulado.saldo_anterior],
        ['Saldo Total Acumulado:', data.balance_acumulado.saldo_total],
        [],
        ['DETALLE DIARIO DE ASISTENCIA'],
        [
            'Fecha', 'Día', 'Tipo', 'Turno Programado', 'Horas Prog.', 'Marc. Ingreso', 'Marc. Salida',
            'Horas Laboradas', 'Horas Extra (+)', 'Horas Debe (-)', 'Tardanza (min)', 'Estado', 'Observaciones'
        ]
    ];

    data.dias_detalle.forEach(d => {
        rows.push([
            d.fecha,
            d.dia_nombre,
            d.es_laboral ? 'Laboral' : (d.es_feriado ? 'Feriado' : 'Descanso'),
            d.turno_label,
            d.horas_programadas,
            d.ingreso || '--',
            d.salida || '--',
            d.horas_laboradas,
            d.horas_extra,
            d.horas_debe,
            d.minutos_tardanza,
            d.estado.toUpperCase(),
            d.observaciones
        ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Balance Asistencia');

    const fileName = `Balance_Asistencia_${data.colaborador.num_doc}_${data.anio}_${data.mes}.xlsx`;
    XLSX.writeFile(wb, fileName);
};

// Imprimir / PDF
const printReport = () => {
    window.print();
};
</script>

<template>
    <Head :title="`Balance Asistencia - ${balanceData?.colaborador?.nombre_completo || 'Colaborador'}`" />

    <BreezeAuthenticatedLayout>
        <div class="max-w-7xl mx-auto space-y-6 pb-12 print:p-0 print:max-w-none">

            <!-- Encabezado Principal (Oculto en Impresión) -->
            <div class="print:hidden">
                <PremiumPageHeader
                    title="Control y Balance de Asistencia por Empleado"
                    subtitle="Gestión detallada de horas programadas, laboradas, horas extras y déficit de asistencia"
                    icon="tabler:user-check"
                >
                    <template #actions>
                        <Link :href="route('rrhh.asistencia-balance-empleados')"
                              class="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold transition-all shadow-xs">
                            <Icon icon="tabler:users-group" class="w-4 h-4 text-indigo-500" />
                            <span>Ver Balance General de Todo el Personal</span>
                        </Link>
                    </template>
                </PremiumPageHeader>
            </div>

            <!-- Filtros de Consulta (Oculto en Impresión) -->
            <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm print:hidden">
                <form @submit.prevent="fetchBalanceData" class="grid grid-cols-1 sm:grid-cols-12 gap-4 items-end">
                    
                    <!-- Selector de Colaborador -->
                    <div class="sm:col-span-5 space-y-1">
                        <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400">
                            Colaborador / Empleado
                        </label>
                        <select v-model="selectedCollaboratorId"
                                class="w-full text-xs px-3.5 py-2.5 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-slate-50/50 dark:bg-slate-850 text-slate-800 dark:text-white font-semibold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all">
                            <option v-for="c in collaborators" :key="c.id" :value="c.id">
                                {{ c.apellidos }} {{ c.nombres }} ({{ c.num_doc || 'S/D' }})
                            </option>
                        </select>
                    </div>

                    <!-- Selector de Mes -->
                    <div class="sm:col-span-3 space-y-1">
                        <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400">
                            Mes
                        </label>
                        <select v-model="selectedMes"
                                class="w-full text-xs px-3.5 py-2.5 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-slate-50/50 dark:bg-slate-850 text-slate-800 dark:text-white font-semibold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all">
                            <option v-for="m in meses" :key="m.value" :value="m.value">
                                {{ m.label }}
                            </option>
                        </select>
                    </div>

                    <!-- Selector de Año -->
                    <div class="sm:col-span-2 space-y-1">
                        <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400">
                            Año
                        </label>
                        <select v-model="selectedAnio"
                                class="w-full text-xs px-3.5 py-2.5 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-slate-50/50 dark:bg-slate-850 text-slate-800 dark:text-white font-semibold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all">
                            <option v-for="a in anios" :key="a" :value="a">{{ a }}</option>
                        </select>
                    </div>

                    <!-- Botón Consultar -->
                    <div class="sm:col-span-2 flex items-center gap-2">
                        <button type="submit" :disabled="loading"
                                class="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-100 dark:shadow-none transition-all hover:scale-[1.01] active:scale-[0.99]">
                            <Icon :icon="loading ? 'tabler:loader' : 'tabler:search'" :class="{ 'animate-spin': loading }" class="w-4 h-4" />
                            <span>{{ loading ? 'Cargando...' : 'Consultar' }}</span>
                        </button>
                    </div>
                </form>
            </div>

            <!-- Loader de estado -->
            <div v-if="loading" class="text-center py-16 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm">
                <Icon icon="tabler:loader-2" class="w-10 h-10 text-indigo-600 animate-spin mx-auto" />
                <p class="text-xs text-slate-400 mt-3 font-semibold">Procesando marcaciones y turnos del colaborador...</p>
            </div>

            <!-- Contenido Principal -->
            <div v-else-if="balanceData" class="space-y-6">

                <!-- Ficha del Colaborador & Barra de Acciones de Exportación -->
                <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                    <div class="flex items-center gap-4">
                        <div class="w-14 h-14 rounded-2xl bg-gradient-to-tr from-indigo-600 to-violet-500 text-white flex items-center justify-center text-lg font-black shadow-md shadow-indigo-100 dark:shadow-none shrink-0">
                            {{ balanceData.colaborador.nombre_completo.charAt(0) }}
                        </div>
                        <div class="space-y-1">
                            <div class="flex flex-wrap items-center gap-2">
                                <h2 class="text-base font-extrabold text-slate-800 dark:text-white">
                                    {{ balanceData.colaborador.nombre_completo }}
                                </h2>
                                <span class="text-[10px] font-mono px-2 py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded font-bold">
                                    DNI: {{ balanceData.colaborador.num_doc }}
                                </span>
                            </div>
                            <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-400 font-medium">
                                <span class="flex items-center gap-1">
                                    <Icon icon="tabler:briefcase" class="w-3.5 h-3.5 text-indigo-500" />
                                    {{ balanceData.colaborador.puesto }}
                                </span>
                                <span class="flex items-center gap-1">
                                    <Icon icon="tabler:clock-play" class="w-3.5 h-3.5 text-emerald-500" />
                                    Turno: {{ balanceData.colaborador.turno_nombre }}
                                </span>
                                <span class="flex items-center gap-1 font-bold text-indigo-600 dark:text-indigo-400">
                                    <Icon icon="tabler:calendar" class="w-3.5 h-3.5" />
                                    Periodo: {{ getMesNombre(balanceData.mes) }} {{ balanceData.anio }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Botones de Exportar -->
                    <div class="flex flex-wrap items-center gap-2.5 print:hidden shrink-0">
                        <button type="button" @click="exportToExcel"
                                class="inline-flex items-center gap-2 px-3.5 py-2.5 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-800 rounded-xl text-xs font-bold transition-all shadow-xs">
                            <Icon icon="tabler:file-spreadsheet" class="w-4 h-4 text-emerald-600" />
                            <span>Exportar Excel</span>
                        </button>
                        <button type="button" @click="printReport"
                                class="inline-flex items-center gap-2 px-3.5 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold transition-all shadow-xs">
                            <Icon icon="tabler:printer" class="w-4 h-4 text-slate-600 dark:text-slate-400" />
                            <span>Imprimir / PDF</span>
                        </button>
                    </div>
                </div>

                <!-- Resumen de Horas (KPIs) -->
                <div class="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    
                    <!-- 1. Horas que debió trabajar -->
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm space-y-1">
                        <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Horas Programadas</span>
                        <div class="text-xl font-black text-slate-800 dark:text-white">
                            {{ balanceData.periodo_actual.total_horas_programadas }} <span class="text-xs font-semibold text-slate-400">hrs</span>
                        </div>
                        <span class="text-[10px] text-slate-400 block">En {{ balanceData.periodo_actual.dias_laborables }} días laborales</span>
                    </div>

                    <!-- 2. Horas Laboradas Reales -->
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm space-y-1">
                        <span class="text-[10px] font-bold uppercase tracking-wider text-indigo-500 block">Horas Laboradas</span>
                        <div class="text-xl font-black text-indigo-600 dark:text-indigo-400">
                            {{ balanceData.periodo_actual.total_horas_laboradas }} <span class="text-xs font-semibold text-indigo-400">hrs</span>
                        </div>
                        <span class="text-[10px] text-slate-400 block">{{ balanceData.periodo_actual.dias_asistidos }} asistencias registradas</span>
                    </div>

                    <!-- 3. Horas a Favor (Extras) -->
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm space-y-1">
                        <span class="text-[10px] font-bold uppercase tracking-wider text-emerald-500 block">Horas a Favor (+)</span>
                        <div class="text-xl font-black text-emerald-600 dark:text-emerald-400">
                            +{{ balanceData.periodo_actual.total_horas_extra }} <span class="text-xs font-semibold text-emerald-400">hrs</span>
                        </div>
                        <span class="text-[10px] text-emerald-500 font-semibold block">Horas extras generadas</span>
                    </div>

                    <!-- 4. Horas Debe (Déficit) -->
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm space-y-1">
                        <span class="text-[10px] font-bold uppercase tracking-wider text-rose-500 block">Horas Debe (-)</span>
                        <div class="text-xl font-black text-rose-600 dark:text-rose-400">
                            -{{ balanceData.periodo_actual.total_horas_debe }} <span class="text-xs font-semibold text-rose-400">hrs</span>
                        </div>
                        <span class="text-[10px] text-rose-500 font-semibold block">{{ balanceData.periodo_actual.dias_faltas }} faltas / tardanzas</span>
                    </div>

                    <!-- 5. Balance Neto del Mes -->
                    <div :class="[
                        balanceData.periodo_actual.saldo_neto >= 0
                            ? 'bg-emerald-50/50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800/80'
                            : 'bg-rose-50/50 border-rose-200 dark:bg-rose-950/20 dark:border-rose-800/80'
                    ]" class="col-span-2 lg:col-span-1 border rounded-2xl p-4 shadow-sm space-y-1">
                        <span class="text-[10px] font-bold uppercase tracking-wider block" :class="balanceData.periodo_actual.saldo_neto >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'">
                            Balance del Mes
                        </span>
                        <div class="text-xl font-black" :class="balanceData.periodo_actual.saldo_neto >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'">
                            {{ balanceData.periodo_actual.saldo_neto >= 0 ? '+' : '' }}{{ balanceData.periodo_actual.saldo_neto }} <span class="text-xs">hrs</span>
                        </div>
                        <span class="text-[10px] font-semibold block text-slate-500 dark:text-slate-400">
                            {{ balanceData.periodo_actual.saldo_neto >= 0 ? 'Saldo a favor' : 'Déficit de horas' }}
                        </span>
                    </div>

                </div>

                <!-- Cuadro Comparativo: Periodo Anterior vs Periodo Actual y Compensación Acumulada -->
                <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-2">
                            <Icon icon="tabler:arrows-diff" class="w-5 h-5 text-indigo-500" />
                            <h3 class="text-sm font-bold text-slate-800 dark:text-white">
                                Resumen Comparativo de Periodos y Registro de Compensación Acumulada
                            </h3>
                        </div>
                        <span class="text-[10px] font-mono uppercase px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-300 rounded font-bold">
                            Balance Histórico
                        </span>
                    </div>

                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4 pt-1">
                        
                        <!-- Columna 1: Periodo Anterior -->
                        <div class="p-4 bg-slate-50/70 dark:bg-slate-950/40 border border-slate-100 dark:border-slate-800/80 rounded-2xl space-y-3">
                            <div class="flex items-center justify-between">
                                <span class="text-xs font-bold text-slate-700 dark:text-slate-200 flex items-center gap-1.5">
                                    <Icon icon="tabler:history" class="w-4 h-4 text-slate-400" />
                                    Periodo Anterior ({{ getMesNombre(balanceData.periodo_previo.mes) }} {{ balanceData.periodo_previo.anio }})
                                </span>
                            </div>
                            <div class="space-y-1.5 text-xs">
                                <div class="flex justify-between text-slate-500">
                                    <span>Horas Programadas:</span>
                                    <span class="font-bold text-slate-700 dark:text-slate-300">{{ balanceData.periodo_previo.totales.total_horas_programadas }} hrs</span>
                                </div>
                                <div class="flex justify-between text-slate-500">
                                    <span>Horas Laboradas:</span>
                                    <span class="font-bold text-slate-700 dark:text-slate-300">{{ balanceData.periodo_previo.totales.total_horas_laboradas }} hrs</span>
                                </div>
                                <div class="flex justify-between text-emerald-600">
                                    <span>Horas Extras (+):</span>
                                    <span class="font-bold">+{{ balanceData.periodo_previo.totales.total_horas_extra }} hrs</span>
                                </div>
                                <div class="flex justify-between text-rose-500">
                                    <span>Horas Debe (-):</span>
                                    <span class="font-bold">-{{ balanceData.periodo_previo.totales.total_horas_debe }} hrs</span>
                                </div>
                                <div class="pt-2 border-t border-slate-200 dark:border-slate-700 flex justify-between font-bold">
                                    <span>Saldo Periodo Anterior:</span>
                                    <span :class="balanceData.periodo_previo.totales.saldo_neto >= 0 ? 'text-emerald-600' : 'text-rose-600'">
                                        {{ balanceData.periodo_previo.totales.saldo_neto >= 0 ? '+' : '' }}{{ balanceData.periodo_previo.totales.saldo_neto }} hrs
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Columna 2: Periodo Actual -->
                        <div class="p-4 bg-indigo-50/40 dark:bg-indigo-950/20 border border-indigo-100 dark:border-indigo-900/40 rounded-2xl space-y-3">
                            <div class="flex items-center justify-between">
                                <span class="text-xs font-bold text-indigo-900 dark:text-indigo-200 flex items-center gap-1.5">
                                    <Icon icon="tabler:calendar-event" class="w-4 h-4 text-indigo-500" />
                                    Periodo Actual ({{ getMesNombre(balanceData.mes) }} {{ balanceData.anio }})
                                </span>
                            </div>
                            <div class="space-y-1.5 text-xs">
                                <div class="flex justify-between text-slate-500">
                                    <span>Horas Programadas:</span>
                                    <span class="font-bold text-slate-700 dark:text-slate-300">{{ balanceData.periodo_actual.total_horas_programadas }} hrs</span>
                                </div>
                                <div class="flex justify-between text-slate-500">
                                    <span>Horas Laboradas:</span>
                                    <span class="font-bold text-slate-700 dark:text-slate-300">{{ balanceData.periodo_actual.total_horas_laboradas }} hrs</span>
                                </div>
                                <div class="flex justify-between text-emerald-600">
                                    <span>Horas Extras (+):</span>
                                    <span class="font-bold">+{{ balanceData.periodo_actual.total_horas_extra }} hrs</span>
                                </div>
                                <div class="flex justify-between text-rose-500">
                                    <span>Horas Debe (-):</span>
                                    <span class="font-bold">-{{ balanceData.periodo_actual.total_horas_debe }} hrs</span>
                                </div>
                                <div class="pt-2 border-t border-indigo-200 dark:border-indigo-800 flex justify-between font-bold">
                                    <span>Saldo Periodo Actual:</span>
                                    <span :class="balanceData.periodo_actual.saldo_neto >= 0 ? 'text-emerald-600' : 'text-rose-600'">
                                        {{ balanceData.periodo_actual.saldo_neto >= 0 ? '+' : '' }}{{ balanceData.periodo_actual.saldo_neto }} hrs
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Columna 3: Compensación y Saldo Acumulado (Diseño Sólido Oscuro de Alto Contraste) -->
                        <div class="p-4 bg-slate-900 dark:bg-slate-850 text-white rounded-2xl space-y-3 shadow-md border border-slate-800">
                            <div class="flex items-center justify-between">
                                <span class="text-xs font-bold flex items-center gap-1.5 text-indigo-300">
                                    <Icon icon="tabler:calculator" class="w-4 h-4 text-indigo-400" />
                                    Balance Acumulado de Horas
                                </span>
                            </div>
                            <div class="space-y-2 text-xs">
                                <div class="flex justify-between text-slate-300">
                                    <span>Saldo Anterior Acumulado:</span>
                                    <span class="font-bold text-white">{{ balanceData.balance_acumulado.saldo_anterior >= 0 ? '+' : '' }}{{ balanceData.balance_acumulado.saldo_anterior }} hrs</span>
                                </div>
                                <div class="flex justify-between text-slate-300">
                                    <span>Saldo del Mes en Curso:</span>
                                    <span class="font-bold text-white">{{ balanceData.balance_acumulado.saldo_actual >= 0 ? '+' : '' }}{{ balanceData.balance_acumulado.saldo_actual }} hrs</span>
                                </div>
                                <div class="pt-2.5 border-t border-slate-800 flex items-center justify-between">
                                    <div class="space-y-0.5">
                                        <span class="text-[10px] uppercase font-bold text-indigo-300 block">Saldo Total Acumulado</span>
                                        <span class="text-[10px] text-slate-400">
                                            {{ balanceData.balance_acumulado.saldo_total >= 0 ? 'Disponible para compensar' : 'Deuda neta de horas' }}
                                        </span>
                                    </div>
                                    <span :class="balanceData.balance_acumulado.saldo_total >= 0 ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40' : 'bg-rose-500/20 text-rose-400 border-rose-500/40'"
                                          class="px-3 py-1.5 rounded-xl border text-sm font-black">
                                        {{ balanceData.balance_acumulado.saldo_total >= 0 ? '+' : '' }}{{ balanceData.balance_acumulado.saldo_total }} hrs
                                    </span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                <!-- Tabs de Navegación (Detalle Diario vs Gráficos) -->
                <div class="flex border-b border-slate-200 dark:border-slate-800 print:hidden">
                    <button type="button" @click="activeTab = 'detalle'"
                            :class="[
                                activeTab === 'detalle'
                                    ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 font-bold border-b-2'
                                    : 'text-slate-400 hover:text-slate-600 font-medium'
                            ]"
                            class="px-5 py-3 text-xs flex items-center gap-2 transition-all">
                        <Icon icon="tabler:table" class="w-4 h-4" />
                        <span>Tabla de Asistencia Diaria</span>
                    </button>
                    <button type="button" @click="activeTab = 'graficos'"
                            :class="[
                                activeTab === 'graficos'
                                    ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 font-bold border-b-2'
                                    : 'text-slate-400 hover:text-slate-600 font-medium'
                            ]"
                            class="px-5 py-3 text-xs flex items-center gap-2 transition-all">
                        <Icon icon="tabler:chart-donut" class="w-4 h-4" />
                        <span>Gráficos y Estadísticas</span>
                    </button>
                </div>

                <!-- TAB 1: TABLA DE DETALLE DIARIO -->
                <div v-show="activeTab === 'detalle' || activeTab === 'all'" class="space-y-4">
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl overflow-hidden shadow-sm">
                        <div class="overflow-x-auto">
                            <table class="w-full text-left border-collapse text-xs">
                                <thead>
                                    <tr class="bg-slate-50/80 dark:bg-slate-850 border-b border-slate-100 dark:border-slate-800 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                        <th class="px-4 py-3.5">Día / Fecha</th>
                                        <th class="px-3 py-3.5">Turno Prog.</th>
                                        <th class="px-3 py-3.5 text-center">Ingreso</th>
                                        <th class="px-3 py-3.5 text-center">Salida</th>
                                        <th class="px-3 py-3.5 text-center">Horas Prog.</th>
                                        <th class="px-3 py-3.5 text-center">Horas Lab.</th>
                                        <th class="px-3 py-3.5 text-center">Extra (+)</th>
                                        <th class="px-3 py-3.5 text-center">Debe (-)</th>
                                        <th class="px-3 py-3.5 text-center">Estado</th>
                                        <th class="px-4 py-3.5">Observaciones</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-slate-50 dark:divide-slate-800/60">
                                    <tr v-for="d in balanceData.dias_detalle" :key="d.fecha"
                                        :class="[
                                            d.estado === 'falta' ? 'bg-rose-50/30 dark:bg-rose-950/10' :
                                            d.estado === 'tardanza' ? 'bg-amber-50/20 dark:bg-amber-950/10' :
                                            d.estado === 'extra' ? 'bg-indigo-50/20 dark:bg-indigo-950/10' : ''
                                        ]"
                                        class="hover:bg-slate-50/50 dark:hover:bg-slate-850/40 transition-colors">
                                        
                                        <!-- Día y Fecha -->
                                        <td class="px-4 py-3 font-semibold text-slate-800 dark:text-slate-200">
                                            <div>{{ d.dia_nombre }}</div>
                                            <div class="text-[9px] font-mono text-slate-400">{{ d.fecha }}</div>
                                        </td>

                                        <!-- Turno Programado -->
                                        <td class="px-3 py-3 text-slate-500 font-mono text-[11px]">
                                            {{ d.turno_label }}
                                        </td>

                                        <!-- Marcación Ingreso -->
                                        <td class="px-3 py-3 text-center font-mono font-bold">
                                            <span v-if="d.ingreso" :class="d.minutos_tardanza > 0 ? 'text-amber-600' : 'text-slate-700 dark:text-slate-200'">
                                                {{ d.ingreso }}
                                            </span>
                                            <span v-else class="text-slate-350 italic">--</span>
                                        </td>

                                        <!-- Marcación Salida -->
                                        <td class="px-3 py-3 text-center font-mono font-bold">
                                            <span v-if="d.salida" class="text-slate-700 dark:text-slate-200">
                                                {{ d.salida }}
                                            </span>
                                            <span v-else class="text-slate-350 italic">--</span>
                                        </td>

                                        <!-- Horas Programadas -->
                                        <td class="px-3 py-3 text-center font-mono text-slate-500">
                                            {{ d.horas_programadas > 0 ? d.horas_programadas : '-' }}
                                        </td>

                                        <!-- Horas Laboradas -->
                                        <td class="px-3 py-3 text-center font-mono font-extrabold text-indigo-600 dark:text-indigo-400">
                                            {{ d.horas_laboradas > 0 ? d.horas_laboradas : '-' }}
                                        </td>

                                        <!-- Horas Extra (+) -->
                                        <td class="px-3 py-3 text-center font-mono font-bold text-emerald-600">
                                            {{ d.horas_extra > 0 ? `+${d.horas_extra}` : '-' }}
                                        </td>

                                        <!-- Horas Debe (-) -->
                                        <td class="px-3 py-3 text-center font-mono font-bold text-rose-500">
                                            {{ d.horas_debe > 0 ? `-${d.horas_debe}` : '-' }}
                                        </td>

                                        <!-- Estado Badge -->
                                        <td class="px-3 py-3 text-center">
                                            <span :class="getStatusBadge(d.estado).class"
                                                  class="px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-wider border">
                                                {{ getStatusBadge(d.estado).label }}
                                            </span>
                                        </td>

                                        <!-- Observaciones -->
                                        <td class="px-4 py-3 text-[11px] text-slate-500 dark:text-slate-400">
                                            {{ d.observaciones || '-' }}
                                        </td>
                                    </tr>
                                </tbody>
                                
                                <!-- Totales del Pie de Tabla -->
                                <tfoot>
                                    <tr class="bg-slate-100/80 dark:bg-slate-850 font-extrabold text-slate-800 dark:text-white border-t-2 border-slate-200 dark:border-slate-700">
                                        <td colspan="4" class="px-4 py-3 text-right uppercase tracking-wider text-[11px]">
                                            Totales del Periodo:
                                        </td>
                                        <td class="px-3 py-3 text-center font-mono">
                                            {{ balanceData.periodo_actual.total_horas_programadas }}
                                        </td>
                                        <td class="px-3 py-3 text-center font-mono text-indigo-600 dark:text-indigo-400">
                                            {{ balanceData.periodo_actual.total_horas_laboradas }}
                                        </td>
                                        <td class="px-3 py-3 text-center font-mono text-emerald-600">
                                            +{{ balanceData.periodo_actual.total_horas_extra }}
                                        </td>
                                        <td class="px-3 py-3 text-center font-mono text-rose-500">
                                            -{{ balanceData.periodo_actual.total_horas_debe }}
                                        </td>
                                        <td colspan="2" class="px-4 py-3 text-left">
                                            <span :class="balanceData.periodo_actual.saldo_neto >= 0 ? 'text-emerald-600' : 'text-rose-600'" class="font-black text-xs">
                                                Saldo Neto: {{ balanceData.periodo_actual.saldo_neto >= 0 ? '+' : '' }}{{ balanceData.periodo_actual.saldo_neto }} hrs
                                            </span>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- TAB 2: GRÁFICOS Y ESTADÍSTICAS -->
                <div v-show="activeTab === 'graficos' || activeTab === 'all'" class="space-y-6">
                    
                    <!-- 1. SEGUIMIENTO DIARIO DE HORAS LABORADAS VS TURNO PLANILLA (Full Width) -->
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                        <div class="flex items-center justify-between border-b border-slate-100 dark:border-slate-800/80 pb-3">
                            <h4 class="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200 flex items-center gap-2">
                                <Icon icon="tabler:chart-arrows" class="w-4 h-4 text-indigo-500" />
                                SEGUIMIENTO DIARIO DE HORAS LABORADAS VS TURNO PLANILLA
                            </h4>
                        </div>
                        <div class="h-72">
                            <MixedBarLineChart :data="balanceData.charts.seguimientoHoras" xTitle="Día del Mes" yTitle="Horas" class="h-full w-full" />
                        </div>
                    </div>

                    <!-- Fila de 3 Gráficos Complementarios -->
                    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        
                        <!-- 2. Minutos de Tardanza por Día -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                            <div class="flex items-center justify-between border-b border-slate-100 dark:border-slate-800/80 pb-3">
                                <h4 class="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                                    <Icon icon="tabler:clock-alert" class="w-4 h-4 text-amber-500" />
                                    MINUTOS DE TARDANZA POR DÍA
                                </h4>
                                <span class="text-xs font-bold text-amber-600">{{ balanceData.periodo_actual.total_minutos_tardanza }} min</span>
                            </div>
                            <div class="h-64">
                                <BarChart :data="balanceData.charts.tardanzasDiarias" xTitle="Día del Mes" yTitle="Minutos" class="h-full w-full" />
                            </div>
                        </div>

                        <!-- 3. Distribución de Horas Mensual -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                            <div class="flex items-center justify-between border-b border-slate-100 dark:border-slate-800/80 pb-3">
                                <h4 class="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                                    <Icon icon="tabler:chart-pie" class="w-4 h-4 text-indigo-500" />
                                    Distribución de Horas Mensual
                                </h4>
                            </div>
                            <div class="h-64 flex items-center justify-center">
                                <DoughnutChart :data="balanceData.charts.distribucionHoras" title="Horas Mensuales" />
                            </div>
                        </div>

                        <!-- 4. Resumen de Estados de Asistencia -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                            <div class="flex items-center justify-between border-b border-slate-100 dark:border-slate-800/80 pb-3">
                                <h4 class="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                                    <Icon icon="tabler:chart-donut" class="w-4 h-4 text-emerald-500" />
                                    Estados de Asistencia (Días)
                                </h4>
                            </div>
                            <div class="h-64 flex items-center justify-center">
                                <DoughnutChart :data="balanceData.charts.resumenEstados" title="Días por Estado" />
                            </div>
                        </div>

                    </div>
                </div>

            </div>

        </div>
    </BreezeAuthenticatedLayout>
</template>

